import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogService } from '../../../../../../../../../@fuse/components/confirm-dialog/confirm-dialog.service';
import { sentry } from '../../../../../../../../../sentry';
import { SearchEquipmentsParams } from '../../../../../../../../core/api/equipments/equipments.type';
import { ActionButton, ActionTypeEnum, TreeViewItem } from '../../../../../../../../core/components/fc-tree-view/fc-tree.types';
import { CustomMessagesService } from '../../../../../../../../core/errors/custom-messages.service';
import { Equipment, Location } from '../../../../../../../../core/interfaces';
import { fetchAllInBatches } from '../../../../../../../../core/utils/fetch-all-in-batches';
import { DialogEquipmentService } from '../../../../../../../equipments/dialog/dialog-equipments.service';
import { EquipmentsServiceApi } from './equipment.api';

@Injectable({
  providedIn: 'root'
})
export class EquipmentLinkService {
  public constructor(
    private readonly equipmentsServiceApi: EquipmentsServiceApi,
    private readonly translateService: TranslateService,
    private readonly dialogEquipmentService: DialogEquipmentService,
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly messageService: CustomMessagesService
  ) {}

  public async searchEquipments(params?: SearchEquipmentsParams): Promise<Equipment[]> {
    return fetchAllInBatches(this.equipmentsServiceApi, params, 500);
  }

  public async searchEquipmentsWithWarranties(params?: SearchEquipmentsParams): Promise<Equipment[]> {
    const pagedResult = await this.equipmentsServiceApi.searchWithWarranties(params);
    return pagedResult.items;
  }

  public formatEquipments(equipments: Equipment[], parent: TreeViewItem): TreeViewItem[] {
    const formattedEquipments: TreeViewItem[] = [];

    if (!equipments) {
      return formattedEquipments;
    }

    for (const equipment of equipments) {
      formattedEquipments.push({
        id: equipment.id,
        text: equipment.name,
        parent: parent,
        description: equipment.number,
        searchTerm: [equipment.name, equipment.number, equipment.notes].filter(Boolean).join(' ').toLowerCase(),
        archived: equipment.archived,
        archivedText: this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.ARCHIVED'),
        icon: 'equipment',
        image: equipment.imageUrl,
        children: [],
        metadata: 'equipment',
        action: {
          type: ActionTypeEnum.menu,
          buttons: this.equipmentButtons(equipment)
        }
      });
    }

    return formattedEquipments;
  }

  private equipmentButtons(equipment: Equipment): ActionButton[] {

    const buttons: ActionButton[] = [
      {
        text: this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.EDIT_EQUIPMENT'),
        icon: 'edit',
        color: '#676767',
        action: async () => {
          await this.openEditEquipmentDialog({ equipment });
        }
      },
      {
        text: equipment.archived ? this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.UNARCHIVE_EQUIPMENT') : this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.ARCHIVE_EQUIPMENT'),
        icon: equipment.archived ? 'unarchive_outline' : 'archive_outline',
        color: '#676767',
        action: async () => {
          await this.openArchiveOrUnarchiveEquipmentDialog({ equipment });
        }
      }
    ];

    const hasSector = equipment.locationSector;
    const hasEnvironment = equipment.locationEnvironment;

    if (!hasSector && !hasEnvironment) {
      return buttons;
    }

    buttons.push({
      text: hasEnvironment ? this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.UNLINK_EQUIPMENT_ENVIRONMENT') : this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.UNLINK_EQUIPMENT_SECTOR'),
      icon: 'delete',
      color: '#676767',
      action: async () => {
        await this.openUnlinkEquipmentDialog({ equipment });
      }
    });

    return buttons;
  }

  public formatUnlinkedEquipments(equipments: Equipment[]): TreeViewItem[] {
    if (!equipments.length) { return []; }

    const treeViewItem: TreeViewItem = {
      text: this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.EQUIPMENTS_WITHOUT_SECTOR_ENVIRONMENT'),
      icon: 'more_vertical',
      archived: false,
      searchTerm: '',
      archivedText: '',
      action: {
        type: ActionTypeEnum.none,
      }
    };

    treeViewItem.children = this.formatEquipments(equipments, treeViewItem);

    return [treeViewItem];
  }

  public async openCreateEquipmentDialog({ location }: { location: Location }): Promise<void> {
    const result = await this.dialogEquipmentService.open({
      equipmentId: null,
      location: location,
      disableEditLocation: true
    });

    if (result && result.reload) {
      window.emitEvent('location-links::reload-tree-view');
    }
  }

  public async openEditEquipmentDialog({ equipment }: { equipment: Equipment }): Promise<void> {
    const result = await this.dialogEquipmentService.open({
      equipmentId: equipment.id,
      location: null
    });

    if (result && result.reload) {
      window.emitEvent('location-links::reload-tree-view');
    }
  }

  private async openArchiveOrUnarchiveEquipmentDialog({ equipment }: { equipment: Equipment }): Promise<void> {

    const result = await this.confirmDialogService.open({
      title: equipment.archived
        ? this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.UNARCHIVE_EQUIPMENT')
        : this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.ARCHIVE_EQUIPMENT'),
      message: equipment.archived
        ? this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.UNARCHIVE_EQUIPMENT_MESSAGE')
        : this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.ARCHIVE_EQUIPMENT_MESSAGE'),
      okText: equipment.archived
        ? this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.UNARCHIVE')
        : this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.ARCHIVE'),
      cancelText: this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.CANCEL')
    });

    if (!result || !result.confirmed) { return; }

    try {

      if (equipment.archived) {
        await this.equipmentsServiceApi.unarchive({
          id: equipment.id
        });

        this.messageService.success(this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.UNARCHIVE_EQUIPMENT_SUCCESS', {
          name: equipment.name
        }));
      } else {
        await this.equipmentsServiceApi.archive({
          id: equipment.id
        });

        this.messageService.success(this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.ARCHIVE_EQUIPMENT_SUCCESS', {
          name: equipment.name
        }));
      }

      window.emitEvent('location-links::reload-tree-view');
    } catch (err) {
      sentry.captureException(err);
      this.messageService.error(err);
    }
  }


  private async openUnlinkEquipmentDialog({ equipment }: { equipment: Equipment }): Promise<void> {

    const result = await this.confirmDialogService.open({
      title: equipment.locationEnvironment
        ? this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.UNLINK_EQUIPMENT_ENVIRONMENT')
        : this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.UNLINK_EQUIPMENT_SECTOR'),
      message: equipment.locationEnvironment
        ? this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.UNLINK_EQUIPMENT_ENVIRONMENT_MESSAGE')
        : this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.UNLINK_EQUIPMENT_SECTOR_MESSAGE'),
      okText: this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.UNLINK'),
      cancelText: this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.CANCEL')
    });

    if (!result || !result.confirmed) { return; }

    try {

      const unlinkEnvironmentParam = {
        equipmentId: equipment.id,
        locationId: equipment.location.id,
        locationSectorId: equipment.locationSector.id,
        locationEnvironmentId: null
      };

      const unlinkSectorParam = {
        equipmentId: equipment.id,
        locationId: equipment.location.id,
        locationSectorId: null,
        locationEnvironmentId: null
      };

      await this.equipmentsServiceApi.linkEquipment({
        items: [
          equipment.locationEnvironment ? unlinkEnvironmentParam : unlinkSectorParam
        ]
      });

      this.messageService.success(this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.UNLINKED_SUCCESS', {
        name: equipment.name
      }));

      window.emitEvent('location-links::reload-tree-view');
    } catch (err) {
      sentry.captureException(err);
      this.messageService.error(err);
    }
  }

}
